import { createSlice } from "@reduxjs/toolkit";
import { getHCFACoordinates, getHCFAMarginValues, getPrinterListData, getPrintSettingDetails, getSingleHCFACoordinates, InlineHCFACoordinates, printDetails, removePrintListItem, singleHCFACoordinateUpdate, updateHCFAMargin, updateHCFAPrintSettings } from "./printConfigAsyncThunkApi";
import i18n from "../../utilities/i18n";
import { PrintListTableBodyData, printListTableData } from "./PrintListTableData";
import { decimalPattern, numericFields, structuredMargin } from "./constants";
import { initialState } from "./initialState";
import { groupCoordinatesBySection } from "./printConfigDataProcessor";

const printConfigSlice = createSlice({
    name: 'printConfigSlice',
    initialState,
    reducers: {

        /**
         * Sets the currently opened printer ID.
         * @param {object} state
         * @param {object} action - Contains the printer ID.
         */

        handleOpenId: (state, action) => {
            state.currOpenId = action.payload
        },

        /**
         * Sets the ID of the item marked for deletion.
         * This is typically used to store which item the user
         * has clicked "Delete" on, before they confirm the action
         * in a modal.
         *
         * @param {Object} state - The current Redux state.
         * @param {Object} action - The Redux action.
         * @param {string | number | null} action.payload - The ID of the item to be deleted.
         */ 

        setIdToDelete: (state, action) => {
            state.idToDelete = action.payload;
        },

        /**
         * Updates the entire table data with the provided payload.
         * @param {object} state
         * @param {object} action - Contains the updated table data array.
         */
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },

        /**
         * Sets whether the Print Configuration tab is visible.
         * @param {object} state - The current Redux state.
         * @param {object} action - The dispatched action containing a boolean payload.
         */
        setShowPrintConfigTab: (state, action) => {
            state.showPrintConfigTab = action.payload;
        },

        /**
         * Sets the current open section state.
         * @param {object} state - The current Redux state.
         * @param {object} action - The dispatched action containing section state data.
         */
        setOpenSection: (state, action) => {
            state.openSection = action.payload;
        },

        /**
         * Sets the expand/collapse all state.
         * @param {object} state - The current Redux state.
         * @param {object} action - The dispatched action containing a boolean value.
         */
        setExpandAll: (state, action) => {
            state.expandAll = action.payload;
        },

        /**
         * Updates the coordinates data in the state.
         * @param {object} state - The current Redux state.
         * @param {object} action - The dispatched action containing coordinates data.
         */
        setCoordinates: (state, action) => {
            state.coordinates = action.payload;
        },

        /**
        * Toggles the expand/collapse state for all sections.
        * @param {object} state - The current Redux state.
        * @param {object} action - The dispatched action containing groupedData (object with section keys).
        * @description Flips the expand state for all sections based on the current expandAll flag.
        */
        handleExpandAll: (state, action) => {
            const groupedData = action.payload; //groupedData object keys
            const newExpandState = !state.expandAll;

            // Expand or collapse all
            const newOpenSection = Object.keys(groupedData).reduce((acc, key) => {
                acc[key] = newExpandState;
                return acc;
            }, {});

            state.expandAll = newExpandState;
            state.openSection = newOpenSection;
        },

        /**
         * Toggles the expand/collapse state for a single section.
         * @param {object} state - The current Redux state.
         * @param {object} action - The dispatched action containing the section name.
         * @description Expands or collapses the selected section and updates the global expandAll flag.
         */
        handleToggleSection: (state, action) => {
            const section = action.payload;

            // Toggle only one section
            const updated = {
                ...state.openSection,
                [section]: !state.openSection[section],
            };

            const allExpanded = Object.values(updated).every(Boolean);

            state.openSection = updated;
            state.expandAll = allExpanded;
        },


        /**
         * Sets the currently selected coordinate ID for single selection.
         * @param {object} state
         * @param {object} action - Contains the selected coordinate ID.
         */
        selectedSingleCoordinateId: (state, action) => {
            state.currentSelectedCoordinateId = action.payload
        },

        /**
         * Updates a specific field in the CMS print coordinates form.
         * Validates numeric and boolean fields before assigning.
         * @param {object} state
         * @param {object} action - Contains field name and value.
         */
        setCoordinateValue: (state, action) => {
            const { name, value } = action.payload;

            if (numericFields.includes(name)) {
                if (value === "" || decimalPattern.test(value)) {
                    state.cmsPrintCoordinates[name] = value;
                }
            }
            else {
                state.cmsPrintCoordinates[name] = value;
            }
        },

        /**
         * Resets all coordinate values to their initial state.
         * @param {object} state
         */
        resetCoordinateValues: (state, action) => {
            if (action.payload.type === "coordinates") {
                const currentSection = state.selectedSection
                const { display_name,y } = state.cmsPrintCoordinates;

                // if current selection is Service Line, while resetting y postion stays same.
                if (currentSection === "Service Line Information") {
                    state.cmsPrintCoordinates = {
                        ...initialState.cmsPrintCoordinates,
                        display_name: display_name,
                        y:y
                    };
                    return;
                }

                state.cmsPrintCoordinates = {
                    ...initialState.cmsPrintCoordinates,
                    display_name: display_name,

                };
            }

            if (action.payload.type === "inlineCoordinate") {
                state.hcfaCoordinates = JSON.parse(JSON.stringify(state.originalCoordinates));
            }
        },

        /**
         * Toggles the open/close state of any modal dynamically.
         * @param {*} state 
         * @param {*} action 
         */
        handleModalToggle: (state, action) => {
            const { modal, value } = action.payload;

            if (modal && Object.hasOwn(state, modal)) {
                state[modal] = value;
            } else {
                console.warn(`Unknown modal key: ${modal}`);
            }
        },

        /**
         * Updates x or y position for a specific coordinate item.
         * @param {object} state
         * @param {object} action - Contains coordinate id, key, and new value.
         */
        updateCoordinatePosition: (state, action) => {
            const { id, key, value } = action.payload;

            // Loop through each section
            Object.keys(state.hcfaCoordinates).forEach((section) => {
                const coordinates = state.hcfaCoordinates[section];
                const coordinate = coordinates.find((item) => item.id === id);

                if (coordinate) {
                    // update x or y
                    coordinate[key] = value;
                    // Mark as edited
                    coordinate.isEdited = true;
                }
            })
        },

        /**
         * Handles both updating and resetting of print configuration form values 
         * for different form types.
         * @param {*} state 
         * @param {*} action 
         */
        handlePrintValuesAction: (state, action) => {
            const { type, name, value } = action.payload;

            if (type === "printMargin" && value !== undefined) {
                state.createEditFormValue[name] = value;
                return;
            }
        },

        /**
         * Updates the search input value for filtering data.
         * @param {object} state
         * @param {object} action - Contains search text.
         */
        setSearchValue: (state, action) => {
            state.searchValue = action.payload;
        },

        /**
         * permission setting
         * @param {*} state 
         * @param {*} action 
         */
        setPermission: (state, action) => {
            state.permission = action.payload;
        },
        /**
         * sets the notification
         * @param {*} state 
         * @param {*} action 
         */
        showNotification: (state, action) => {
            state.notifyMessage = action.payload.message;
            state.showNotification = true;
            state.statusTag = action.payload.status;
        },
        /**
         * sets search value
         * @param {*} state 
         * @param {*} action 
         */
        setAppliedSearchValue(state, action) {
            state.appliedSearchValue = action.payload;
        },

        /**
         * Sets the expansion state based on filtered search results.
         * This ensures that only sections containing search results are expanded.
         * @param {Object} state - The current Redux state.
         * @param {Object} action - The Redux action.
         * @param {Object} action.payload - The filtered data object, where keys are section names.
         */
        setFilteredExpansion: (state, action) => {
            const groupedData = action.payload;

            // Create a new openSection state
            const newOpenSection = Object.keys(groupedData).reduce((acc, key) => {
                acc[key] = true; // Always set to true (expanded)
                return acc;
            }, {});

            state.openSection = newOpenSection;
            state.expandAll = Object.keys(groupedData).length > 0;
        },
        
        /**
         * resets notification.`
         * @param {*} state 
         * @param {*} action 
         */
        resetNotifyWindow: (state) => {
            state.notifyMessage = "";
            state.statusTag = "";
            state.showNotification = false
        },

        /**
         * Sets the currently selected section in the state.
         * @param {Object} state - The current Redux state.
         * @param {Object} action - The Redux action.
         * @param {*} action.payload - The value of the section to be set (e.g., a section name or ID).
         */
        setSelectedSection: (state, action) => {
            state.selectedSection = action.payload;
        },
        
        /**
         * Merges new values into the `printFormValues` state object.
         * This allows updating one or more form fields at a time without
         * overwriting the entire `printFormValues` object.
         * @param {Object} state - The current Redux state.
         * @param {Object} action - The Redux action.
         * @param {Object} action.payload - An object containing the fields to update (e.g., { name: 'New Name' }).
         */
        setPrintFormValues: (state, action) => {
            state.printFormValues = {
                ...state.printFormValues,
                ...action.payload,
            };
        },

        /**
         * Conditionally resets specific parts of the state to their initial values
         * based on the `type` provided in the action payload.
         * @param {Object} state - The current Redux state.
         * @param {Object} action - The Redux action.
         * @param {Object} action.payload - An object containing a `type` property.
         * @param {'margin' | 'printer'} action.payload.type - The specific part of the state to reset.
         */
        resetPrintFormValues: (state, action) => {
            if (action.payload.type === "margin") {
                state.createEditFormValue = initialState.createEditFormValue
            }

            if (action.payload.type === "printer") {
                state.printFormValues = {
                    name: "",
                    is_target_printer: false,
                    remarks: "",
                }
            }
        },
        toggleSearchFlag: (state, action) => {
            state.searchValueFlag = action.payload?.search
            state.loading = action.payload?.loading
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        clearSearch: (state) => {
            state.searchValue = "";
            state.appliedSearchValue = "";
        }

    },

    extraReducers: (builder) => {

        builder.addCase(getPrinterListData.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(getPrinterListData.fulfilled, (state, action) => {
            state.loading = false;
            state.tableData = action.payload.rawData; // raw API data
            printListTableData.tableBodyData = action.payload.tableBodyData; // formatted table data

        });

        builder.addCase(getPrinterListData.rejected, (state) => {
            state.loading = false
            state.tableData = [];
            printListTableData.tableBodyData = PrintListTableBodyData;
        });

        builder.addCase(printDetails.pending, (state) => {
            state.loading = true
        })
        builder.addCase(printDetails.fulfilled, (state) => {
            state.loading = false
            state.notifyMessage = i18n.t('systemConfigs.successAddMsg');
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.success");
        })
        builder.addCase(printDetails.rejected, (state, action) => {
            state.loading = false
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        })

        builder.addCase(getHCFACoordinates.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(getHCFACoordinates.fulfilled, (state, action) => {
            state.loading = false;
            const { grouped } = groupCoordinatesBySection(action.payload);

            state.hcfaCoordinates = grouped;
            state.originalCoordinates = JSON.parse(JSON.stringify(grouped)); // deep copy
        })

        builder.addCase(getHCFACoordinates.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(removePrintListItem.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(removePrintListItem.fulfilled, (state, action) => {
            state.loading = false;
            state.tableData = action.payload;
            state.notifyMessage = i18n.t("systemConfigs.removedSuccess");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.success");
        })

        builder.addCase(removePrintListItem.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(getSingleHCFACoordinates.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(getSingleHCFACoordinates.fulfilled, (state, action) => {
            state.loading = false;
            state.cmsPrintCoordinates = action.payload;

        })

        builder.addCase(getSingleHCFACoordinates.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.notifyMessage = action.payload;
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(singleHCFACoordinateUpdate.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(singleHCFACoordinateUpdate.fulfilled, (state,) => {
            state.loading = false;
            state.cmsPrintCoordinatesModal = false
            state.notifyMessage = i18n.t("commons.successUpdation");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.success");
        })

        builder.addCase(singleHCFACoordinateUpdate.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(getHCFAMarginValues.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(getHCFAMarginValues.fulfilled, (state, action) => {
            state.loading = false;
            state.createEditFormValue = structuredMargin(action.payload)
        })

        builder.addCase(getHCFAMarginValues.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(updateHCFAMargin.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(updateHCFAMargin.fulfilled, (state) => {
            state.loading = false;
            state.notifyMessage = i18n.t("commons.successUpdation");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.success");
        })

        builder.addCase(updateHCFAMargin.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(getPrintSettingDetails.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(getPrintSettingDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.printFormValues = action.payload
        })

        builder.addCase(getPrintSettingDetails.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(updateHCFAPrintSettings.pending, (state) => {
            state.loading = true;
        })

        builder.addCase(updateHCFAPrintSettings.fulfilled, (state) => {
            state.loading = false;
            state.printFormValues = initialState.printFormValues
        });

        builder.addCase(updateHCFAPrintSettings.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload;
            state.notifyMessage = action.payload || i18n.t("systemConfigs.errormessages.somethingWrong");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.error");
        });

        builder.addCase(InlineHCFACoordinates.pending, (state) => {
            state.loading = true;
        })
        builder.addCase(InlineHCFACoordinates.fulfilled, (state) => {
            state.loading = false;
            
            // Once the save is successful, mark all edited coordinates as clean
            Object.values(state.hcfaCoordinates).forEach((sectionItems) => {
                sectionItems.forEach((item) => {
                    if (item.isEdited) {
                        item.isEdited = false;
                    }
                });
            });

            state.notifyMessage = i18n.t("systemConfigs.successUpdate");
            state.showNotification = true;
            state.statusTag = i18n.t("systemConfigs.success");
        })
        builder.addCase(InlineHCFACoordinates.rejected, (state, action) => {
            state.loading = false;
            state.notifyMessage = action.payload;
            state.showNotification = true;
            state.statusTag = "error";
        });
    }
})

export const { updatePrintValue, handleEditClick, setPermission, resetNotifyWindow, handlePrintValues, resetPrintValues, handleOpenId, selectedSingleCoordinateId, updateCoordinatePosition,
    setCoordinateValue, resetCoordinateValues, setShowNotify, handleEditPrintValues, resetEditPrintValues, handleSearchInput, setSearchValue,setSelectedSection,setLoading,
    setShowPrintConfigTab, setActiveTab, handleModalToggle, setOpenSection, setExpandAll, setCoordinates, handleToggleSection, handleExpandAll, handlePrintValuesAction,
    resetPrintFormValues, setPrintFormValues,showNotification,setAppliedSearchValue, setFilteredExpansion, setIdToDelete, toggleSearchFlag,clearSearch
} = printConfigSlice.actions;
export default printConfigSlice.reducer;